import React from 'react';
import { format } from 'date-fns';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import VirtualizedTable from '../table/VirtualizedTable';
import { ActionsData, Columns, DataFromColumns } from '../../types/reports';
import { CancellationWaitlistEntryRow } from '../../types/cancellation-waitlist';
import {
  formatWaitlistDays,
  formatWaitlistTimes,
  formatWaitlistWindow,
} from '../../helpers/cancellation-waitlist';
import { getServiceLabel } from '../../helpers/services';

interface Props {
  entries: CancellationWaitlistEntryRow[];
  totalCount?: number;
  isLoading: boolean;
  showProvider: boolean;
  onOpenEntry: (entry: CancellationWaitlistEntryRow) => void;
  onNotify: (entry: CancellationWaitlistEntryRow) => void;
  onRemove: (entry: CancellationWaitlistEntryRow) => void;
  onOpenSettings: () => void;
  onEndReached: () => void | Promise<void>;
}

function WaitlistTable({
  entries,
  totalCount,
  isLoading,
  showProvider,
  onOpenEntry,
  onNotify,
  onRemove,
  onOpenSettings,
  onEndReached,
}: Props) {
  const data = React.useMemo(
    () =>
      entries.map(entry => {
        const actions: ActionsData = {
          actions: [
            { label: 'View details', action: () => onOpenEntry(entry) },
            {
              label: 'Notify about an opening',
              action: () => onNotify(entry),
            },
            { label: 'Remove from waitlist', action: () => onRemove(entry) },
          ],
        };

        return {
          provider: entry.employeeDisplayName,
          customer: entry.customerDisplayName,
          // Guarded: rows from a pre-preferences API have no serviceCategory,
          // and getServiceLabel would silently default it to Tattoo.
          service: entry.serviceCategory
            ? getServiceLabel(entry.serviceCategory, null)
            : '—',
          dates: formatWaitlistWindow(entry),
          daysAndTimes: `${formatWaitlistDays(entry)} · ${formatWaitlistTimes(
            entry,
          )}`,
          joined: format(new Date(entry.joinedAt), 'M/d/yy'),
          lastNotified: entry.lastNotifiedAt
            ? format(new Date(entry.lastNotifiedAt), "M/d/yy 'at' h:mm a")
            : 'Never',
          actions,
          entry,
        };
      }),
    [entries, onNotify, onOpenEntry, onRemove],
  );

  const columns = [
    {
      dataKey: 'provider',
      label: 'Provider',
      width: 'auto',
      hidden: !showProvider,
    },
    { dataKey: 'customer', label: 'Customer', width: 'auto' },
    { dataKey: 'service', label: 'Service', width: 'auto' },
    { dataKey: 'dates', label: 'Dates', width: 'auto' },
    { dataKey: 'daysAndTimes', label: 'Days & Times', width: 'auto' },
    { dataKey: 'joined', label: 'Joined', width: 'auto' },
    { dataKey: 'lastNotified', label: 'Last Notified', width: 'auto' },
    { dataKey: 'actions', label: '', width: 100, hidden: !data.length },
  ] as const satisfies Columns;

  return (
    <VirtualizedTable
      type="regular"
      columns={columns}
      data={data as unknown as DataFromColumns<typeof columns>[]}
      height={520}
      isLoading={isLoading}
      emptyText="No waitlist members yet."
      headerContent={{
        title: 'Waitlist',
        subtitle: totalCount ? `${totalCount} entries` : undefined,
        action: {
          label: 'Waitlist Settings',
          icon: <SettingsOutlinedIcon />,
          onClick: onOpenSettings,
        },
      }}
      onRowClick={row => onOpenEntry((row as (typeof data)[number]).entry)}
      onEndReached={onEndReached}
    />
  );
}

export default WaitlistTable;
